/**
 * Account-surface presentation formatters.
 *
 * Pure, defensive helpers that turn admin DTOs into display strings and badge
 * presentations (mirrors web/src/features/api-keys/apiKeyFormatters.ts and
 * web/src/lib/statusMapping.ts conventions). They never throw, never fabricate
 * values, and unknown backend values always degrade to a neutral "Unknown"
 * presentation rather than implying health or privilege.
 */
import type { BadgeVariant } from '@/components/ui/badge';
import type {
  APIKeyView,
  EntitlementView,
  LifecycleStatus,
  UsageGranularity,
  UsageHistoryPoint,
} from '@/api/admin-types';

export interface StatusPresentation {
  /** Short label for badges / inline tags. */
  label: string;
  /** Badge variant; neutral outline for unknown values. */
  variant: BadgeVariant;
}

/* ----- Dates / times ----- */

/** "Aug 31, 2026, 3:04 PM" (viewer locale). Em-dash for null/invalid. */
export function formatDateTime(iso: string | null | undefined): string {
  const label = formatWith(iso, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return label;
}

/** "Aug 31, 2026" (viewer locale). Em-dash for null/invalid. */
export function formatDate(iso: string | null | undefined): string {
  return formatWith(iso, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatWith(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  try {
    return new Date(t).toLocaleString(undefined, opts);
  } catch {
    return '—';
  }
}

/* ----- Money ----- */

/**
 * Format a billing-audit amount. `amount_cents` is minor units and 0 means
 * "not applicable" per the contract — rendered as "—" so a lifecycle event is
 * never mistaken for a free charge. `currency` is an ISO 4217 code.
 */
export function formatMoney(amountCents: number | null | undefined, currency: string | null | undefined): string {
  if (amountCents === null || amountCents === undefined) return '—';
  if (amountCents === 0) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
    }).format(amountCents / 100);
  } catch {
    // Unknown currency code: show the raw minor units, never throw.
    return `${currency ?? ''} ${amountCents}`.trim();
  }
}

/* ----- Numbers ----- */

export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined).format(n);
  } catch {
    return String(n);
  }
}

/** "12,345 of 40,000" or "12,345 (no limit)". */
export function formatUsedOverLimit(used: number | null | undefined, limit: number | null | undefined): string {
  if (limit === null || limit === undefined) return `${formatNumber(used)} (no limit)`;
  return `${formatNumber(used)} of ${formatNumber(limit)}`;
}

/* ----- Status → badge presentations ----- */

const LIFECYCLE: Record<LifecycleStatus, StatusPresentation> = {
  active: { label: 'Active', variant: 'success' },
  disabled: { label: 'Disabled', variant: 'destructive' },
  deleted: { label: 'Deleted', variant: 'outline' },
};

/** Account/user lifecycle status. Unknown values degrade to a neutral badge. */
export function presentLifecycleStatus(status: string | null | undefined): StatusPresentation {
  const known = status ? LIFECYCLE[status as LifecycleStatus] : undefined;
  return known ?? { label: status ? titleCase(status) : 'Unknown', variant: 'outline' };
}

/** Entitlement snapshot source (none | stripe | manual). */
export function presentEntitlementSource(source: string | null | undefined): StatusPresentation {
  switch (source) {
    case 'stripe':
      return { label: 'Stripe', variant: 'info' };
    case 'manual':
      return { label: 'Manual', variant: 'warning' };
    case 'none':
      return { label: 'None', variant: 'outline' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
}

/**
 * Stripe subscription status (the FINANCIAL state — distinct from runtime
 * access, which is the entitlement `effective` flag).
 */
export function presentSubscriptionStatus(status: string | null | undefined): StatusPresentation {
  switch (status) {
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'trialing':
      return { label: 'Trial', variant: 'info' };
    case 'past_due':
      return { label: 'Past due', variant: 'warning' };
    case 'unpaid':
      return { label: 'Unpaid', variant: 'destructive' };
    case 'canceled':
      return { label: 'Canceled', variant: 'outline' };
    case 'paused':
      return { label: 'Paused', variant: 'outline' };
    case 'incomplete':
      return { label: 'Incomplete', variant: 'warning' };
    case 'incomplete_expired':
      return { label: 'Incomplete (expired)', variant: 'outline' };
    default:
      return { label: status ? titleCase(status) : 'None', variant: 'outline' };
  }
}

/** Runtime access state from the billing summary (active|grace|lapsed|none). */
export function presentAccessState(state: string | null | undefined): StatusPresentation {
  switch (state) {
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'grace':
      return { label: 'Grace period', variant: 'warning' };
    case 'lapsed':
      return { label: 'Lapsed', variant: 'destructive' };
    case 'none':
      return { label: 'No access', variant: 'outline' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
}

/**
 * Derived runtime effect of the entitlement snapshot. `effective` is null for
 * source=none (Basic); Basic is always effective by contract, so a null
 * derived flag on a none-source snapshot reads as effective in practice — but
 * we present the flag truthfully rather than re-deriving authority client-side.
 */
export function presentEffectiveState(entitlement: EntitlementView | null | undefined): StatusPresentation {
  if (!entitlement) return { label: 'No snapshot', variant: 'outline' };
  if (entitlement.effective === true) return { label: 'Effective', variant: 'success' };
  if (entitlement.effective === false) return { label: 'Not effective', variant: 'destructive' };
  return { label: 'Effective (unknown)', variant: 'outline' };
}

/** API key status (the contract has no persisted "expired" enum). */
export function presentKeyStatus(status: string | null | undefined): StatusPresentation {
  switch (status) {
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'revoked':
      return { label: 'Revoked', variant: 'destructive' };
    default:
      return { label: 'Unknown', variant: 'outline' };
  }
}

/* ----- Labels ----- */

/** Title-case a token like "past_due" → "Past Due". */
export function titleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Plan transition "Basic → Pro"; em-dash for a null old plan (initial event). */
export function formatPlanTransition(oldPlan: string | null | undefined, newPlan: string | null | undefined): string {
  const from = oldPlan && oldPlan.length > 0 ? oldPlan : '—';
  const to = newPlan && newPlan.length > 0 ? newPlan : '—';
  return `${from} → ${to}`;
}

/** Reason transition "— → manual_grant"; em-dashes for nulls. */
export function formatReasonTransition(
  oldReason: string | null | undefined,
  newReason: string | null | undefined,
): string {
  const from = oldReason && oldReason.length > 0 ? oldReason : '—';
  const to = newReason && newReason.length > 0 ? newReason : '—';
  return `${from} → ${to}`;
}

/**
 * Period label for a usage-history bucket: "Aug 31, 2026" for daily rows and
 * "Aug 2026" for monthly rows, per the contract's date vs year/month split.
 */
export function formatHistoryPeriod(point: UsageHistoryPoint, granularity: UsageGranularity): string {
  if (granularity === 'monthly') {
    if (point.year === null || point.year === undefined || point.month === null || point.month === undefined) {
      return '—';
    }
    try {
      return new Date(Date.UTC(point.year, point.month - 1, 1)).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });
    } catch {
      return `${point.year}-${point.month}`;
    }
  }
  return formatDate(point.date);
}

/**
 * Reconstruct a safe, recognizable key descriptor from the non-secret
 * `environment` field + the backend `preview` suffix (same approach as web's
 * apiKeyFormatters — never recomposes secret material).
 */
export function formatKeyPreview(key: Pick<APIKeyView, 'environment' | 'preview'>): string {
  if (key.preview && key.preview.includes('eisk_')) return key.preview;
  const env = key.environment ?? 'live';
  const suffix = key.preview && key.preview.length > 0 ? key.preview : '…';
  return `eisk_${env}_eik_${suffix}`;
}

/** Friendly environment label. */
export function formatEnvironment(env: string | null | undefined): string {
  if (!env) return '—';
  return env.charAt(0).toUpperCase() + env.slice(1);
}
